import React from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { useGeneralTrends, GeneralTrendDataPoint } from '../hooks/useGeneralTrends';
import { HabitLog } from '../../types';
import { useTheme } from '../hooks/useTheme';

interface GeneralTrendChartProps {
  logs: HabitLog[];
  habitId: string;
  language: 'en' | 'ar';
}

const CustomTooltip = ({ active, payload, label, language, palette }: any) => {
  if (!active || !payload || !payload.length) return null;

  const dataPoint = payload[0]?.payload as GeneralTrendDataPoint | undefined;
  if (!dataPoint) return null;

  return (
    <div className="bg-surface/95 backdrop-blur-sm rounded-lg p-3 shadow-xl border border-separator">
      <p className="text-xs text-gray-400 mb-2 font-medium">{label}</p>
      <p className="text-[10px] text-gray-500 mb-2 uppercase tracking-wider">
        {language === 'ar' ? 'متوسط 7 أيام' : '7-Day Average'}
      </p>
      <div className="space-y-1.5">
        <div className="flex items-center gap-2 text-xs">
          <span
            className="w-2 h-2 rounded-full"
            style={{ backgroundColor: palette.takbirah.bright }}
          />
          <span className="text-gray-300 flex-1">
            {language === 'ar' ? 'معدل النجاح' : 'Success Rate'}
          </span>
          <span className="font-bold tabular-nums text-emerald-400">
            {Math.round(dataPoint.successRate * 100)}%
          </span>
        </div>
        <div className="text-[10px] text-gray-500 mt-1">
          {language === 'ar' 
            ? `${dataPoint.rawDone} من ${dataPoint.rawTotal} يوم`
            : `${dataPoint.rawDone} of ${dataPoint.rawTotal} days`}
        </div>
      </div>
    </div>
  );
};

const GeneralTrendChart: React.FC<GeneralTrendChartProps> = ({ logs, habitId, language }) => {
  const data = useGeneralTrends(logs, habitId);
  const { palette } = useTheme();

  const tickInterval = Math.max(1, Math.floor(data.length / 12));

  return (
    <div className="w-full">
      <div className="w-full h-40 -mx-4">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart
            data={data}
            margin={{ top: 5, right: 5, left: 5, bottom: 5 }}
          >
            <XAxis
              dataKey="displayDate"
              tick={{ fontSize: 9, fill: palette.text3 }}
              tickLine={false}
              axisLine={false}
              interval={tickInterval}
            />
            <YAxis
              domain={[0, 1]}
              tick={{ fontSize: 9, fill: palette.text3 }}
              tickLine={false}
              axisLine={false}
              width={25}
              ticks={[0, 0.5, 1]}
              tickFormatter={(v) => `${Math.round(v * 100)}%`}
            />
            <Tooltip content={<CustomTooltip language={language} palette={palette} />} />
            
            <Line
              type="monotone"
              dataKey="successRate"
              stroke={palette.takbirah.bright}
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 4, strokeWidth: 0 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default GeneralTrendChart;
